import { computed, makeObservable } from 'mobx'

import { Model } from 'modules/core/lib/mobx-rest'

import { ICustomField } from 'modules/custom_resources/store/models/customFields/Field'
import forms from 'modules/surveys/store/collections/forms'
import groups from 'modules/surveys/store/collections/groups'
import type Form from 'modules/surveys/store/models/Form'
import type Group from 'modules/surveys/store/models/Group'

export interface IQuestionGroup {
  id: number
  drafted_at: string
  answered_at: string
  created_at: string
  end_on: string
  surveys_form_id: number
  respondent_group_id: number
  target_group_id: number
  surveys_question_group_config_id: number
  respondent_id: number
  target_id: number
  target: unknown
  state: 'active' | 'closed' | 'completed' | 'draft'
  questions: Array<{
    custom_field: ICustomField
    position: number
    surveys_question_category_id: number
  }>
  can_see_answers: boolean
  answers: Array<{ surveys_question_id: number }>
  cycle_id: number | null
  editable_until: string | null
}

class QuestionGroup extends Model<IQuestionGroup> {
  constructor(attr: IQuestionGroup) {
    super(attr)
    makeObservable(this, {
      isAnswered: computed,
      isClosed: computed,
      isActive: computed,
      isDraft: computed,
      targetGroup: computed,
      respondentGroup: computed,
      form: computed,
      canSeeAnswers: computed,
      withCategory: computed,
      withoutCategory: computed,
    })
  }

  get indexes() {
    return ['respondent_id', 'surveys_form_id']
  }

  static urlRoot() {
    return '/surveys/question_groups'
  }

  get isAnswered(): boolean {
    return this.attributes.state === 'completed'
  }

  get isClosed(): boolean {
    return this.attributes.state === 'closed'
  }

  get isActive(): boolean {
    return this.attributes.state === 'active'
  }

  get isDraft(): boolean {
    return this.attributes.state === 'draft'
  }

  get targetGroup(): Group | null | undefined {
    return groups.get(this.attributes.target_group_id)
  }

  get respondentGroup(): Group | null | undefined {
    return groups.get(this.attributes.respondent_group_id)
  }

  get form(): Form | null | undefined {
    return forms.find({
      id: this.attributes.surveys_form_id,
    })
  }

  get canSeeAnswers(): boolean {
    return this.attributes.can_see_answers
  }

  get withCategory() {
    return this.attributes.questions.filter((question) => question.surveys_question_category_id)
  }

  get withoutCategory() {
    return this.attributes.questions.filter((question) => !question.surveys_question_category_id)
  }
}

export default QuestionGroup
